import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@velocity/ui';
import IBAN from 'iban';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import { PrivateCustomer } from '../../../../types/instant-lease-api';
import { includesAny } from '../../../../utils/helper';
import { formatBankDetails } from '../../../../utils/sanitize-data';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import { ibanFormat } from '../../../common/input-field/iban-field';
import { FormSection, FormTextField } from '../../../common/velocity-form';

interface BankTemplateDataProps {
  onSubmit: (owner: BankTemplateData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
}

export interface BankTemplateData {
  account_owner: string;
  iban: string;
}

const BankTemplate: React.FC<BankTemplateDataProps> = ({
  privateCustomer,
  onSubmit,
  tabButton,
  loading,
}) => {
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  const [loadingWeiter, setLoadingWeiter] = useState<boolean>(
    loading ? loading : false,
  );

  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // validationSchema
  const validationSchema = yup.object().shape({
    account_owner: yup.string().trim().required('Pflichtfeld'),
    iban: yup
      .string()
      .required('Pflichtfeld')
      .max(34)
      .test(
        'test-iban',
        'Diese IBAN ist unbekannt. Bitte prüfen.',
        (iban: any) => {
          return iban && IBAN.isValid(iban) && iban.match(/DE|de+/g);
        },
      ),
  });

  const defaultOwner =
    privateCustomer?.bank_account?.account_owner ||
    [privateCustomer?.person?.first_name, privateCustomer?.person?.last_name]
      .filter(Boolean)
      .join(' ');

  // Form hook
  const {
    setValue,
    register,
    control,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<BankTemplateData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      account_owner: defaultOwner,
      iban: privateCustomer?.bank_account?.iban,
    },
  });

  // Names may be entered after mount (co-applicant tabs)
  useEffect(() => {
    setValue('account_owner', defaultOwner);
  }, [defaultOwner, setValue]);

  // Submit Data
  const submitData = async (data: BankTemplateData) => {
    setLoadingWeiter(true);
    formatBankDetails(data.iban)
      .then((response) => {
        const corpatrationBanks = [
          'Postbank Ndl der Deutsche Bank',
          'DSL Bank Ndl der Deutsche Bank',
          'Postbank/DSL Ndl der Deutsche Bank',
          'Deutsche Bank',
          'Deutsche Bank Europe',
        ];
        clearErrors();
        if (
          activeApplication?.loyalty_card?.loyalty_card_type?.toLowerCase() ===
          'postbank'
        ) {
          includesAny(corpatrationBanks, [response.bank_name])
            ? onSubmit({
                iban: data.iban,
                account_owner: data.account_owner,
              })
            : setError('iban', {
                type: 'custom',
                message:
                  'Zur Nutzung der Vorteilskondition muss der Rateneinzug über ein Konto bei der Postbank/Deutsche Bank erfolgen.',
              });
        } else {
          onSubmit({
            iban: data.iban,
            account_owner: data.account_owner,
          });
        }
      })
      .catch(() => {
        setError('iban', {
          type: 'custom',
          message:
            'Wir konnten diese IBAN keiner Bank zuordnen. Bitte prüfe diese noch einmal',
        });
        setLoadingWeiter(false);
      });
  };

  return (
    <form onSubmit={handleSubmit(submitData)} noValidate>
      <div className='flex w-full flex-col gap-6'>
        <FormSection title='Bankverbindung'>
          <FormTextField
            label='Name des Kontoinhabers'
            required
            disabled
            defaultValue={defaultOwner}
            {...register('account_owner')}
            errorMessage={errors.account_owner?.message}
          />
          <Controller
            name='iban'
            control={control}
            render={({ field, fieldState }) => (
              <FormTextField
                label='IBAN'
                required
                autoComplete='off'
                errorMessage={fieldState.error?.message}
                {...field}
                value={ibanFormat(field.value ?? '')}
                onChange={(e) =>
                  field.onChange(
                    e.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase(),
                  )
                }
              />
            )}
          />
        </FormSection>
      </div>

      <>
        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next'
                type='back-next'
                data-testid='disabled-forward-button'
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading: loadingWeiter,
                  onClick: handleSubmit(submitData),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  variant='secondary-outlined'
                  size='m'
                  loading={loadingWeiter}
                  onClick={handleSubmit(submitData)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading: loadingWeiter,
              dataTestId: 'start-fetching',
              children: 'nächster Schritt',
            }}
          />
        )}
      </>
    </form>
  );
};

export default BankTemplate;
